import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, Messaging, onMessage } from 'firebase/messaging';
import stringify from 'json-stable-stringify';
import { lastValueFrom } from 'rxjs';
import { environment as Config } from '@env/environment';
import { Nullable } from '@shared/models/types';
import { LoggerService } from '@core/log/services/logger';
import { BackgroundMessageEvent, MessageBody, MessagePayloadExtended } from '@core/notifications/firebase-push.model';
import { PushApi } from '@core/notifications/services/push-api';

@Injectable({ providedIn: 'root' })
export class FirebasePushMgr {
  private readonly logger = inject(LoggerService);
  private readonly pushService = inject(PushApi);
  private readonly afMessaging: Messaging;
  private readonly message = signal<Nullable<MessageBody>>(null);
  private serviceWorkerRegistration: Nullable<ServiceWorkerRegistration>;

  constructor() {
    const app = initializeApp(Config.firebaseConfig, 'Tag App Web');
    this.afMessaging = getMessaging(app);

    onMessage(this.afMessaging, (payload) => {
      const message = payload as unknown as MessagePayloadExtended;
      if (message.data.notificationOrigin && location.hostname === new URL(message.data.notificationOrigin).hostname) {
        this.message.set({ message, fromServiceWorker: false });
      }
    });

    navigator.serviceWorker.addEventListener('message', ({ data }: BackgroundMessageEvent) => {
      const condition =
        data &&
        data.type === 'FIREBASE_BG_MESSAGE' &&
        data.payload.data.notificationOrigin &&
        location.hostname === new URL(data.payload.data.notificationOrigin).hostname;

      if (condition) {
        this.message.set({ message: data.payload, fromServiceWorker: true });
      }
    });
  }

  public get serviceWorker() {
    return this.serviceWorkerRegistration;
  }

  public get messageEvent() {
    return this.message.asReadonly();
  }

  public async requestPermission(user: number) {
    this.logger.log('Requesting notifications permission...');
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      this.logger.log('Notification permission granted.');
      const fileName = 'main-sw.js';

      const serviceWorkerRegistration = await navigator.serviceWorker.register(
        isDevMode() ? `/${fileName}` : `/tagappweb/${fileName}`
      );

      this.serviceWorkerRegistration = serviceWorkerRegistration;

      getToken(this.afMessaging, { vapidKey: Config.firebaseVapidKey, serviceWorkerRegistration })
        .then(async (pushChannel) => {
          try {
            const thumbmarkModule = await import('@thumbmarkjs/thumbmarkjs');
            const installationId = await thumbmarkModule.getThumbmark().then((r) => r.thumbmark);
            if (pushChannel && installationId) {
              await lastValueFrom(this.pushService.installation(installationId, pushChannel, user));
              this.logger.log(`Notification registration token: ${pushChannel}`);
            } else {
              this.logger.warn(`
                No registration token/installationId available. Request permission to generate one.

                Registration token: ${pushChannel}

                InstallationId: ${installationId}
              `);
            }
          } catch (err: unknown) {
            this.logger.error(stringify(err) ?? '');
          }
        })
        .catch((err) => {
          this.logger.error(stringify(err) ?? '');
        });
    } else {
      this.logger.warn('Notification permission were not granted.');
    }
  }
}
